import './styles.css';
import { getLevelById, loadContent } from './content';
import { createPuzzleGame, type PuzzleHandle } from './game/createPuzzleGame';
import {
  isLevelUnlocked,
  loadProgress,
  recordCompletion,
  resetProgress,
  saveProgress,
} from './progress';
import type { GameContent, LevelDefinition, ProgressV1, RunStats } from './types';

declare global {
  interface Window {
    __MIST_HARBOR_TEST__?: {
      openBoard: () => void;
      startLevel: (id: string) => void;
      solveCurrent: () => void;
      getView: () => string;
      getProgress: () => ProgressV1;
    };
  }
}

const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('缺少应用挂载节点');
const app = appElement;

let content: GameContent;
let progress = loadProgress();
let currentLevel: LevelDefinition | null = null;
let puzzleHandle: PuzzleHandle | null = null;
let currentView = 'loading';
let moves = 0;
let hints = 0;
let startedAt = 0;
let elapsedSeconds = 0;
let timerId: number | null = null;

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character] ?? character);
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function imagePath(level: LevelDefinition): string {
  return level.puzzle.image;
}

function finalPieceForLevel(levelId: string) {
  return content.finalPuzzle.pieces.find((piece) => piece.levelId === levelId);
}

function renderAnniversaryCelebration(): string {
  const letters = Array.from('两周年快乐')
    .map((letter, index) => `<span aria-hidden="true" style="--letter-index:${index}">${letter}</span>`)
    .join('');
  const confetti = Array.from({ length: 14 }, (_, index) => (
    `<i aria-hidden="true" style="--confetti-index:${index};--confetti-x:${(index * 37) % 100}%;--confetti-rotate:${(index * 47) % 180}deg"></i>`
  )).join('');
  return `<div class="anniversary-celebration" data-anniversary-message aria-label="两周年快乐">
    <div class="anniversary-message">${letters}</div>
    <div class="anniversary-confetti">${confetti}</div>
  </div>`;
}

function renderFinalMosaic(celebrate = false): string {
  const completedCount = Object.keys(progress.completed).length;
  const complete = completedCount === content.levels.length;
  const pieces = [...content.finalPuzzle.pieces]
    .sort((left, right) => left.position - right.position)
    .map((piece) => {
      const unlocked = Boolean(progress.completed[piece.levelId]);
      const level = getLevelById(content, piece.levelId);
      return `<div class="final-mosaic-piece ${unlocked ? 'is-unlocked' : 'is-locked'}" data-piece-level="${escapeHtml(piece.levelId)}" style="--piece-index:${piece.position}">
        ${unlocked
          ? `<img data-fallback src="${escapeHtml(piece.image)}" alt="${escapeHtml(piece.alt)}" />`
          : `<span aria-label="${escapeHtml(level?.dossier ?? piece.levelId)}尚未解锁">${escapeHtml(level?.order.toString().padStart(2, '0') ?? '?')}</span>`}
      </div>`;
    }).join('');
  return `
    <section class="final-mosaic-panel ${complete ? 'is-complete' : ''} ${complete && celebrate ? 'is-celebrating' : ''}" aria-label="终章拼图">
      <div class="final-mosaic-copy">
        <p class="eyebrow">FINAL MOSAIC · ${completedCount} / 6</p>
        <h2>${escapeHtml(content.finalPuzzle.title)}</h2>
        <p>${escapeHtml(complete ? content.finalPuzzle.caption : '每完成一关，就会有一块真实影像回到这里。集齐六块后才能看见完整照片。')}</p>
      </div>
      <div class="final-mosaic-grid" style="--mosaic-columns:${content.finalPuzzle.columns};--mosaic-rows:${content.finalPuzzle.rows}">${pieces}${complete && celebrate ? renderAnniversaryCelebration() : ''}</div>
    </section>`;
}

function renderPieceUnlock(level: LevelDefinition, isLast: boolean): string {
  const piece = finalPieceForLevel(level.id);
  if (!piece) return '';
  if (isLast) {
    return `<section class="piece-unlock is-final">
      <p class="eyebrow">FINAL PIECE · 06 / 06</p>
      <h2>最后一块已经归位</h2>
      <p>六块影像已经拼成完整照片。</p>
      ${renderFinalMosaic(true)}
    </section>`;
  }
  return `<section class="piece-unlock" data-unlocked-piece="${escapeHtml(level.id)}">
    <div class="piece-unlock-copy">
      <p class="eyebrow">PUZZLE PIECE · ${level.order.toString().padStart(2, '0')} / 06</p>
      <h2>获得一块终章拼图</h2>
      <p>这块影像已放回档案板；还缺 ${content.levels.length - level.order} 块。</p>
    </div>
    <figure class="piece-photo"><img data-fallback src="${escapeHtml(piece.image)}" alt="${escapeHtml(piece.alt)}" /></figure>
  </section>`;
}

function setView(name: string): void {
  currentView = name;
  window.scrollTo({ top: 0, behavior: 'instant' });
}

function stopTimer(): void {
  if (timerId !== null) window.clearInterval(timerId);
  timerId = null;
}

function destroyPuzzle(): void {
  stopTimer();
  puzzleHandle?.destroy();
  puzzleHandle = null;
}

function startTimer(): void {
  stopTimer();
  startedAt = performance.now();
  elapsedSeconds = 0;
  timerId = window.setInterval(() => {
    elapsedSeconds = Math.floor((performance.now() - startedAt) / 1000);
    const element = document.querySelector<HTMLElement>('[data-stat="time"]');
    if (element) element.textContent = formatTime(elapsedSeconds);
  }, 250);
}

function renderLoading(): void {
  setView('loading');
  app.innerHTML = `
    <main class="loading-screen">
      <div class="loading-seal" aria-hidden="true">M</div>
      <p class="eyebrow">正在打开档案箱</p>
      <h1>雾港档案</h1>
      <div class="loading-line" aria-hidden="true"><span></span></div>
    </main>`;
}

function renderError(error: unknown): void {
  destroyPuzzle();
  setView('error');
  const message = error instanceof Error ? error.message : '发生了未知错误';
  app.innerHTML = `
    <main class="error-screen">
      <section class="paper-panel error-panel">
        <p class="eyebrow">档案无法读取</p>
        <h1>这一页被潮水打湿了</h1>
        <p>${escapeHtml(message)}</p>
        <button class="primary-button" data-action="reload">重新尝试</button>
      </section>
    </main>`;
  document.querySelector('[data-action="reload"]')?.addEventListener('click', () => window.location.reload());
}

function renderTitle(): void {
  destroyPuzzle();
  setView('title');
  const completedCount = Object.keys(progress.completed).length;
  const actionLabel = completedCount > 0 ? '继续查阅' : '打开档案箱';
  const hero = content.levels[0] ? imagePath(content.levels[0]) : '';
  app.innerHTML = `
    <main class="title-screen" style="--hero-image: url('${escapeHtml(hero)}')">
      <div class="title-vignette"></div>
      <section class="title-copy">
        <p class="eyebrow">${escapeHtml(content.game.eyebrow)}</p>
        <div class="title-rule" aria-hidden="true"><span>◆</span></div>
        <h1>${escapeHtml(content.game.title)}</h1>
        <p class="tagline">${escapeHtml(content.game.tagline)}</p>
        <p class="opening">${escapeHtml(content.game.opening)}</p>
        <div class="title-actions">
          <button class="primary-button large" data-action="open-board">${actionLabel}</button>
          <span class="local-note">调查进度仅保存在这台设备</span>
        </div>
      </section>
      <aside class="case-stamp" aria-label="当前进度">
        <span>CASE FILE</span>
        <strong>${completedCount.toString().padStart(2, '0')} / 06</strong>
        <small>${completedCount === 6 ? '全部归档' : '调查进行中'}</small>
      </aside>
    </main>`;
  document.querySelector('[data-action="open-board"]')?.addEventListener('click', renderBoard);
}

function renderBoard(): void {
  destroyPuzzle();
  currentLevel = null;
  setView('board');
  const completedCount = Object.keys(progress.completed).length;
  const cards = content.levels.map((level) => {
    const unlocked = isLevelUnlocked(content.levels, level.id, progress);
    const record = progress.completed[level.id];
    const status = record ? '已归档' : unlocked ? '待复核' : '封存中';
    const buttonLabel = record ? '重新查阅' : '开始调查';
    return `
      <article class="dossier-card ${unlocked ? '' : 'is-locked'} ${record ? 'is-complete' : ''}">
        <div class="dossier-image-wrap">
          <img class="dossier-image" src="${escapeHtml(imagePath(level))}" alt="" loading="lazy" />
          <span class="dossier-number">${level.order.toString().padStart(2, '0')}</span>
          <span class="dossier-status">${status}</span>
        </div>
        <div class="dossier-copy">
          <p class="eyebrow">${escapeHtml(level.dossier)}</p>
          <h2>${escapeHtml(level.title)}</h2>
          <p>${escapeHtml(level.subtitle)}</p>
          ${record ? `<dl class="best-record"><div><dt>步数</dt><dd>${record.bestMoves}</dd></div><div><dt>用时</dt><dd>${formatTime(record.bestTimeSeconds)}</dd></div><div><dt>提示</dt><dd>${record.fewestHints}</dd></div></dl>` : ''}
          <button class="card-button" data-level-id="${escapeHtml(level.id)}" ${unlocked ? '' : 'disabled'}>
            ${unlocked ? buttonLabel : '需先完成上一档案'}
          </button>
        </div>
      </article>`;
  }).join('');

  app.innerHTML = `
    <main class="board-screen">
      <header class="board-header">
        <button class="text-button" data-action="title" aria-label="返回封面">← 返回封面</button>
        <div>
          <p class="eyebrow">INVESTIGATION BOARD</p>
          <h1>档案索引</h1>
          <p>完成一份，下一份才会揭开封条。</p>
        </div>
        <div class="progress-medallion"><strong>${completedCount}</strong><span>/ 6 已归档</span></div>
      </header>
      ${renderFinalMosaic()}
      <section class="dossier-grid" aria-label="六份案件档案">${cards}</section>
      <footer class="board-footer">
        <p>${completedCount === 6 ? '六份档案均已归档，完整影像已经复原。' : '所有记录只保存在当前浏览器中。'}</p>
        <button class="danger-button" data-action="reset-progress">重置全部进度</button>
      </footer>
    </main>`;

  document.querySelector('[data-action="title"]')?.addEventListener('click', renderTitle);
  document.querySelectorAll<HTMLButtonElement>('[data-level-id]').forEach((button) => {
    button.addEventListener('click', () => startLevel(button.dataset.levelId ?? ''));
  });
  document.querySelector('[data-action="reset-progress"]')?.addEventListener('click', () => {
    if (window.confirm('确定要清除六关的全部完成记录吗？此操作无法撤销。')) {
      progress = resetProgress();
      renderBoard();
    }
  });
  bindImageFallbacks();
}

function startLevel(levelId: string): void {
  const level = getLevelById(content, levelId);
  if (!level || !isLevelUnlocked(content.levels, level.id, progress)) return;
  destroyPuzzle();
  currentLevel = level;
  moves = 0;
  hints = 0;
  setView('puzzle');
  app.innerHTML = `
    <main class="puzzle-screen">
      <header class="puzzle-header">
        <button class="text-button" data-action="board" aria-label="返回档案索引">← 档案索引</button>
        <div class="puzzle-heading">
          <p class="eyebrow">${escapeHtml(level.dossier)} · ${escapeHtml(level.subtitle)}</p>
          <h1>${escapeHtml(level.title)}</h1>
        </div>
        <div class="run-stats" aria-label="本次调查数据">
          <div><span>步数</span><strong data-stat="moves">0</strong></div>
          <div><span>用时</span><strong data-stat="time">00:00</strong></div>
          <div><span>提示</span><strong data-stat="hints">0</strong></div>
        </div>
      </header>
      <section class="puzzle-layout">
        <div class="puzzle-stage-wrap">
          <div id="puzzle-stage" class="puzzle-stage"></div>
          <p id="puzzle-status" class="puzzle-status" role="status">${escapeHtml(level.instructions)}</p>
        </div>
        <aside class="puzzle-tools paper-panel">
          <p class="eyebrow">调查指引</p>
          <h2>复原这一页</h2>
          <p class="instructions">${escapeHtml(level.instructions)}</p>
          <div class="tool-buttons">
            <button class="secondary-button" data-action="hint">给我一点提示</button>
            <button class="secondary-button" data-action="reset-puzzle">重置本关</button>
          </div>
          <p class="keyboard-note">画布获得焦点后，可使用 Tab、Enter 或方向键辅助操作。</p>
        </aside>
      </section>
    </main>`;

  document.querySelector('[data-action="board"]')?.addEventListener('click', renderBoard);
  document.querySelector('[data-action="hint"]')?.addEventListener('click', () => {
    hints += 1;
    updateStat('hints', hints);
    puzzleHandle?.hint();
  });
  document.querySelector('[data-action="reset-puzzle"]')?.addEventListener('click', () => {
    moves = 0;
    hints = 0;
    updateStat('moves', 0);
    updateStat('hints', 0);
    puzzleHandle?.reset();
    startTimer();
  });

  const stage = document.querySelector<HTMLElement>('#puzzle-stage');
  if (!stage) return;
  puzzleHandle = createPuzzleGame(stage, level, {
    onMove: (value) => {
      moves = value;
      updateStat('moves', value);
    },
    onStatus: updateStatus,
    onComplete: finishLevel,
    onError: (message) => renderError(new Error(message)),
  });
  startTimer();
  window.setTimeout(() => stage.querySelector('canvas')?.focus(), 150);
}

function updateStat(name: 'moves' | 'hints', value: number): void {
  const element = document.querySelector<HTMLElement>(`[data-stat="${name}"]`);
  if (element) element.textContent = String(value);
}

function updateStatus(message: string, tone: 'normal' | 'good' | 'bad' = 'normal'): void {
  const element = document.querySelector<HTMLElement>('#puzzle-status');
  if (!element) return;
  element.textContent = message;
  element.dataset.tone = tone;
}

function finishLevel(): void {
  if (!currentLevel) return;
  elapsedSeconds = Math.max(1, Math.floor((performance.now() - startedAt) / 1000));
  stopTimer();
  const stats: RunStats = { moves: Math.max(1, moves), timeSeconds: elapsedSeconds, hints };
  progress = recordCompletion(progress, currentLevel.id, stats);
  saveProgress(progress);
  window.setTimeout(() => renderReward(currentLevel!, stats), 160);
}

function renderReward(level: LevelDefinition, stats: RunStats): void {
  destroyPuzzle();
  setView('reward');
  const reward = level.reward;
  const hasImage = reward.mode === 'image' || reward.mode === 'mixed';
  const hasText = reward.mode === 'text' || reward.mode === 'mixed';
  const isLast = level.order === content.levels.length;
  const isLongImage = ['scroll-hotspot', 'vertical-strip', 'photo-sequence', 'scroll-quiz', 'photo-code'].includes(level.kind);
  app.innerHTML = `
    <main class="reward-screen">
      <section class="reward-card ${reward.mode}">
        <div class="reward-pin" aria-hidden="true"></div>
        <header class="reward-header">
          <p class="eyebrow">UNLOCKED · ${escapeHtml(level.dossier)}</p>
          <span class="reward-seal">已归档</span>
          <h1>${escapeHtml(reward.title)}</h1>
        </header>
        ${renderPieceUnlock(level, isLast)}
        ${hasImage ? `<figure class="reward-figure ${isLongImage ? 'is-long' : ''}"><img data-fallback src="${escapeHtml(reward.image ?? '')}" alt="${escapeHtml(reward.alt ?? '')}" /><figcaption>${escapeHtml(reward.caption ?? '')}</figcaption></figure>` : ''}
        ${hasText ? `<blockquote class="reward-quote"><span aria-hidden="true">“</span><p>${escapeHtml(reward.body ?? '')}</p></blockquote>` : ''}
        ${!hasImage && reward.caption ? `<p class="reward-caption">${escapeHtml(reward.caption)}</p>` : ''}
        <dl class="reward-stats">
          <div><dt>本次步数</dt><dd>${stats.moves}</dd></div>
          <div><dt>调查用时</dt><dd>${formatTime(stats.timeSeconds)}</dd></div>
          <div><dt>使用提示</dt><dd>${stats.hints}</dd></div>
        </dl>
        <div class="reward-actions">
          <button class="secondary-button" data-action="board">返回档案板</button>
          ${isLast ? '' : '<button class="primary-button" data-action="next">下一份档案</button>'}
        </div>
        ${isLast ? '<p class="ending-note">六份档案与六块影像全部归位，完整照片已保存到档案板。</p>' : ''}
      </section>
    </main>`;

  document.querySelector('[data-action="board"]')?.addEventListener('click', renderBoard);
  document.querySelector('[data-action="next"]')?.addEventListener('click', () => {
    const next = content.levels[level.order];
    if (next) startLevel(next.id);
  });
  if (isLast) {
    window.setTimeout(() => {
      document.querySelector('.final-mosaic-panel.is-celebrating')?.classList.add('is-celebrated');
    }, 3000);
  }
  bindImageFallbacks();
}

function bindImageFallbacks(): void {
  document.querySelectorAll<HTMLImageElement>('img').forEach((image) => {
    image.addEventListener('error', () => {
      image.classList.add('image-missing');
      image.alt = image.alt || '图片暂时无法读取';
      image.closest('figure, .dossier-image-wrap, .final-mosaic-piece')?.classList.add('has-missing-image');
    }, { once: true });
  });
}

function installTestBridge(): void {
  if (new URLSearchParams(window.location.search).get('test') !== '1') return;
  window.__MIST_HARBOR_TEST__ = {
    openBoard: renderBoard,
    startLevel,
    solveCurrent: () => puzzleHandle?.solveForTest(),
    getView: () => currentView,
    getProgress: () => progress,
  };
}

async function bootstrap(): Promise<void> {
  renderLoading();
  try {
    content = await loadContent();
    progress = loadProgress();
    installTestBridge();
    renderTitle();
  } catch (error) {
    renderError(error);
  }
}

void bootstrap();
